import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { customersService } from '@/services/customersService'
import { branchService } from '@/services/branchService'
import { BranchEntity, Customer, CreditNote, CreditNoteLine } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { formatCalendarDate } from '@/lib/calendarDate'
import { getBranchCurrency, getBranchCurrencyWords } from '@/lib/currency'

const formatDate = (value?: string) => formatCalendarDate(value)

const formatAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const buildAddress = (entity?: Partial<BranchEntity> | Partial<Customer> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}

// number-to-words helpers
const ONES = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
]
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
const SCALE = ['', 'Thousand', 'Million', 'Billion', 'Trillion']

const convertHundreds = (num: number): string => {
  let result: string[] = []
  if (num >= 100) {
    result.push(`${ONES[Math.floor(num / 100)]} Hundred`)
    num %= 100
  }
  if (num >= 20) {
    result.push(TENS[Math.floor(num / 10)])
    if (num % 10) result.push(ONES[num % 10])
  } else if (num > 0) {
    result.push(ONES[num])
  }
  return result.join(' ')
}

const convertIntegerToWords = (value: number) => {
  if (value === 0) return ONES[0]
  let num = value
  const chunks: string[] = []
  let scaleIndex = 0
  while (num > 0) {
    const chunk = num % 1000
    if (chunk) {
      const chunkWords = convertHundreds(chunk)
      const scaleWord = SCALE[scaleIndex]
      chunks.unshift(`${chunkWords}${scaleWord ? ` ${scaleWord}` : ''}`.trim())
    }
    num = Math.floor(num / 1000)
    scaleIndex++
  }
  return chunks.join(' ')
}

const amountToWords = (amount?: number) => {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return ''
  const absolute = Math.abs(amount)
  const integerPart = Math.floor(absolute)
  const fractionPart = Math.round((absolute - integerPart) * 100)
  const integerWords = convertIntegerToWords(integerPart)
  const { major, minor } = getBranchCurrencyWords()
  let words = `${integerWords} ${major}`
  if (fractionPart > 0) words += ` and ${convertIntegerToWords(fractionPart)} ${minor}`
  return `${words} Only`
}

const findQrImage = async (creditNoteData: any): Promise<string | null> => {
  if (!creditNoteData?.qrCode) return null
  try {
    const QRCode = await import('qrcode')
    const qrCodeData = String(creditNoteData.qrCode)
    const dataUrl = await QRCode.toDataURL(qrCodeData, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 280,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
    return dataUrl
  } catch (error) {
    console.error('QR gen error', error)
    return null
  }
}

export default function CreditNotePrint(): JSX.Element {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creditNote, setCreditNote] = useState<CreditNote | null>(null)
  const [lines, setLines] = useState<CreditNoteLine[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [branch, setBranch] = useState<BranchEntity | null>(null)
  const [qrImage, setQrImage] = useState<string | null>(null)
  const [branchCityName, setBranchCityName] = useState<string>('')
  const [customerCityName, setCustomerCityName] = useState<string>('')
  const [invoiceIssueDate, setInvoiceIssueDate] = useState<string>('')

  useEffect(() => {
    if (!branchId || !id) {
      setError('Missing branch or credit note identifier')
      setLoading(false)
      return
    }
    const creditNoteId = Number(id)
    if (Number.isNaN(creditNoteId)) {
      setError('Invalid credit note id')
      setLoading(false)
      return
    }

    const loadCreditNote = async () => {
      try {
        setLoading(true)
        setError(null)
        console.log('Loading credit note for print:', { branchId, creditNoteId })
        const creditNoteResponse = await creditNotesService.getCreditNoteById(branchId, creditNoteId)
        console.log('Credit note response:', creditNoteResponse)
        console.log('Credit note lines:', creditNoteResponse.creditNoteLines)
        console.log('Customer ID:', creditNoteResponse.customerId)
        
        setCreditNote(creditNoteResponse)
        // Show all lines, even if some fields are missing
        const allLines = creditNoteResponse.creditNoteLines || []
        console.log('All lines count:', allLines.length)
        console.log('Lines data:', allLines)
        // Only filter out completely empty lines (no description, no qty, no price)
        const validLines = allLines.filter(
          (line: CreditNoteLine) => 
            (line.description && line.description.trim() !== '') ||
            (line.qty && line.qty > 0) ||
            (line.unitPrice && line.unitPrice > 0) ||
            (line.itemPrice && line.itemPrice > 0)
        )
        console.log('Valid lines count after filtering:', validLines.length)
        setLines(validLines.length > 0 ? validLines : allLines) // Show all lines if filtering removes everything
        
        const [customerData, branchData, qrData] = await Promise.all([
          creditNoteResponse.customerId ? customersService.getCustomerById(branchId, creditNoteResponse.customerId).catch((err) => {
            console.error('Failed to load customer:', err)
            return null
          }) : null,
          branchService.getBranchById(branchId).catch((err) => {
            console.error('Failed to load branch:', err)
            return null
          }),
          findQrImage(creditNoteResponse),
        ])
        console.log('Customer data:', customerData)
        console.log('Branch data:', branchData)
        setCustomer(customerData)
        setBranch(branchData)
        setQrImage(qrData)

        // Load invoice issue date if refInvId exists
        if (creditNoteResponse.refInvId) {
          try {
            const { invoicesService } = await import('@/services/invoicesService')
            const invoiceData = await invoicesService.getInvoiceById(branchId, creditNoteResponse.refInvId)
            setInvoiceIssueDate(invoiceData.invoiceDate || '')
          } catch (err) {
            console.error('Failed to load invoice date', err)
          }
        }

        if (branchData?.country && branchData?.city) {
          try {
            const branchCities = await branchService.getCityLookups(branchData.country)
            const branchCity = branchCities.find((c: any) => c.id === branchData.city)
            if (branchCity) setBranchCityName(branchCity.lookupNameEn || '')
          } catch (err) { console.error('branch city', err) }
        }

        if (customerData?.country && customerData?.city) {
          try {
            const customerCities = await customersService.getCities(customerData.country)
            const customerCity = customerCities.find((c: any) => c.id === customerData.city)
            if (customerCity) setCustomerCityName(customerCity.lookupNameEn || '')
          } catch (err) { console.error('customer city', err) }
        }
      } catch (err: any) {
        console.error('Failed to load credit note for print', err)
        const message = err?.response?.data?.message || err?.message || 'Unable to load credit note details'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    loadCreditNote()
  }, [branchId, id])

  const totals = useMemo(() => {
    const validLines = lines || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice ?? ((line.qty || 0) * (line.unitPrice || 0))), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount ?? 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount ?? 0), 0)
    const vatAmount = validLines.reduce((sum, line) => sum + (line.taxAmount ?? 0), 0)
    const grandTotal = creditNote?.total ?? validLines.reduce((sum, line) => sum + (line.lineAmount ?? line.taxableAmount ?? 0), 0)
    return { subTotal, totalDiscount, taxableAmount, vatAmount, total: grandTotal }
  }, [lines, creditNote])

  const amountWords = useMemo(() => amountToWords(totals.total), [totals.total])

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Preparing credit note for print...</p>
      </div>
    </div>
  )

  if (error || !creditNote) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
      <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Credit note not found'}</p>
      <Button onClick={() => navigate('/credit-notes')}>Back to Credit Notes</Button>
    </div>
  )

  return (
    <div className="invoice-print-root p-6 bg-gray-50 print:bg-white print:p-0 print:m-0">
      <style>{`
        @media print {
          @page { 
            size: A4; 
            margin: 8mm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: auto;
            overflow: hidden;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important }
          .invoice-print-root {
            margin: 0;
            padding: 0;
            height: 100vh;
            border: 1px solid #ddd;
            position: relative;
          }
          .invoice-wrapper {
            border: 1px solid #ddd;
            padding: 10px;
            min-height: calc(100vh - 16mm);
            display: flex;
            flex-direction: column;
            position: relative;
            padding-bottom: 30px;
            margin: 0;
          }
          .invoice-content {
            flex: 1;
          }
          table { 
            page-break-inside: auto;
          }
          tr { 
            page-break-inside: avoid;
            page-break-after: auto;
          }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .invoice-footer {
            position: fixed;
            bottom: 8mm;
            left: 8mm;
            right: 8mm;
            border-top: 1px dashed #999;
            padding-top: 4px;
            padding-bottom: 4px;
            text-align: center;
            font-weight: 600;
            font-size: 9px;
            background: white;
            z-index: 1000;
          }
        }

        .invoice-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937; border: 1px solid #ddd; }
        .invoice-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: none; min-height: calc(100vh - 40px); display: flex; flex-direction: column; }
        .invoice-content { flex: 1; }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px }
        .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
        .invoice-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; }

        .info-shell { width:100%; border-collapse:collapse; table-layout:fixed; margin-bottom: 4px }
        .info-shell > tbody > tr > td { border:none; padding:0; vertical-align:middle }
        .info-shell-main { width: auto }
        .info-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:10px; margin-bottom: 0 }
        .info-table td { vertical-align:middle; padding:5px 8px; border:1px solid #F5F5F5 }
        .info-label { width:25%; background:#F5F5F5; font-weight:700; text-align:left }
        .info-value { width:50%; text-align:center }
        .info-label-ar { width:25%; background:#F5F5F5; font-weight:700; text-align:right }
        .info-qr { width:108px; padding:6px !important; border:1px solid #F5F5F5 !important; text-align:center; vertical-align:middle; background:#fff }

        .party-box { border:1px solid #F5F5F5; border-radius:4px; padding:6px }
        .party-box.gray { background:#f6f6f6 }
        .party-title { font-weight:700; margin-bottom:4px; font-size:10px }
        .party-row { display:flex; gap:4px; margin-bottom:3px }

        .items-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px }
        .items-table th, .items-table td { border:1px solid #F5F5F5; padding:3px; vertical-align:middle; }
        .items-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
        .items-table tbody tr td { vertical-align:middle; font-size:9px }
        .items-total-row { background:#F5F5F5; font-weight:800 }

        .summary-box { border:1px solid #F5F5F5; padding:6px; border-radius:4px; font-size:9px }

        .invoice-footer { border-top:1px dashed #999; padding-top:4px; margin-top:4px; text-align:center; font-weight:600; font-size:9px }
        @media print {
          .invoice-footer {
            position: fixed;
            bottom: 8mm;
            left: 8mm;
            right: 8mm;
            border-top: 1px dashed #999;
            padding-top: 4px;
            text-align: center;
            font-weight: 600;
            font-size: 9px;
            background: white;
            z-index: 1000;
          }
        }

        @media screen {
          .invoice-wrapper { max-width: 820px; padding: 12px }
          .top-brand { margin-bottom: 6px }
          .company-title { font-size: 16px }
          .invoice-main-title { font-size: 18px; margin-top: 6px }
          .divider-strong { margin: 8px 0 }
          .info-table { font-size: 11px; margin-bottom: 6px }
          .info-table td { padding: 4px }
          .items-table { font-size: 10px; margin-top: 6px }
          .items-table th, .items-table td { padding: 4px }
          .items-table thead th { font-size: 10px }
          .items-table tbody tr td { font-size: 10px }
          .summary-box { padding: 8px; font-size: 10px }
          .invoice-footer { padding-top: 6px; margin-top: 6px; font-size: 10px }
        }

        .rtl { direction: rtl; unicode-bidi: embed }

        .qr-image { width:96px; height:96px; max-width:100%; object-fit:contain; display:block; margin:0 auto; border:1px solid #F5F5F5 }
        @media print {
          .info-qr { width:96px }
          .qr-image { width:84px; height:84px }
        }

      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Credit Note
        </Button>
      </div>

      <div className="invoice-wrapper">
        <div className="invoice-content">
        {/* header */}
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>CR No:</strong> {branch?.crNo || '-'}</div>
            <div><strong>VAT No:</strong> {branch?.vatNo || '-'}</div>
          </div>

          <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <img src={getLogoPath()} alt="Default Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}</div>
            <div><strong>رقم ضريبة القيمة المضافة:</strong> {branch?.vatNo || '-'}</div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        {/* Credit Note Heading */}
        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="company-title print:text-base" style={{ fontSize: 18, fontWeight: 700, marginBottom: 3 }}>اشعار دائن</div>
          <div className="company-title print:text-sm" style={{ fontSize: 16, fontWeight: 700 }}>Credit Note</div>
        </div>

        {/* Credit Note info: EN | value | AR — QR sits beside only when present */}
        <table className="info-shell print:mb-2" style={{ marginBottom: 6 }}>
          <colgroup>
            <col />
            {qrImage ? <col style={{ width: 112 }} /> : null}
          </colgroup>
          <tbody>
            <tr>
              <td className="info-shell-main">
                <table className="info-table">
                  <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '25%' }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td className="info-label">Credit Note #:</td>
                      <td className="info-value">{creditNote.creditNoteNumber || creditNote.creditNoteNo || `CN-${creditNote.creditNoteId}`}</td>
                      <td className="info-label-ar rtl"><strong>رقم الإشعار:</strong></td>
                    </tr>
                    <tr>
                      <td className="info-label">Credit Note Date:</td>
                      <td className="info-value">{formatDate(creditNote.creditNoteDate)}</td>
                      <td className="info-label-ar rtl"><strong>تاريخ الإشعار:</strong></td>
                    </tr>
                    <tr>
                      <td className="info-label">Invoice #:</td>
                      <td className="info-value">{creditNote.refInvNumber || '-'}</td>
                      <td className="info-label-ar rtl"><strong>رقم الفاتورة:</strong></td>
                    </tr>
                    <tr>
                      <td className="info-label">Invoice Issue Date:</td>
                      <td className="info-value">{formatDate(invoiceIssueDate) || '-'}</td>
                      <td className="info-label-ar rtl"><strong>تاريخ إصدار الفاتورة:</strong></td>
                    </tr>
                    <tr>
                      <td className="info-label">Supply Date:</td>
                      <td className="info-value">{formatDate(creditNote.supplyDate) || '-'}</td>
                      <td className="info-label-ar rtl"><strong>تاريخ التوريد:</strong></td>
                    </tr>
                  </tbody>
                </table>
              </td>
              {qrImage ? (
                <td className="info-qr">
                  <img src={qrImage} alt="qr" className="qr-image" />
                </td>
              ) : null}
            </tr>
          </tbody>
        </table>

        {/* Seller / Buyer Table */}
        <table className="seller-buyer-table print:mb-2" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #F5F5F5', marginBottom: 4 }}>
          <thead>
            <tr>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'left', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={2}>SELLER</th>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }}></th>
              <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={3}>BUYER</th>
            </tr>
          </thead>
          <tbody>
            {/* Name Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%' }}><strong>Name:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {branch?.nameEn || '-'}
                {branch?.nameAr && <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{branch.nameAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%' }}><strong>الاسم:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%' }}><strong>Name:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                {customer?.customerNameEn || customer?.customerNameAr || 'Customer'}
                {customer?.customerNameAr && <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{customer.customerNameAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%' }}><strong>الاسم:</strong></td>
            </tr>
            {/* Address Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Address:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                {buildAddress(branch) || '-'}
                {branch?.addressAr && <div className="rtl print:text-[10px]" style={{ fontSize: 9, marginTop: 1, textAlign: 'right' }}>{branch.addressAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>عنوان:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Address:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                {customer?.address || customer?.addressEn || customer?.addressAr || '-'}
                {customer?.addressAr && <div className="rtl print:text-[10px]" style={{ fontSize: 9, marginTop: 1, textAlign: 'right' }}>{customer.addressAr}</div>}
              </td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>عنوان:</strong></td>
            </tr>
            {/* Country Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Country:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.countryName || 'Saudi Arabia (SA)'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>دولة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Country:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.countryAr || 'Saudi Arabia (SA)'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>دولة:</strong></td>
            </tr>
            {/* City Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>City:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branchCityName || branch?.cityName || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>مدينة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>City:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customerCityName || customer?.cityName || customer?.cityNameAr || customer?.cityAr || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>مدينة:</strong></td>
            </tr>
            {/* Zip/Postal Code Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Zip/Postal Code:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.zipCode || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>الرمز / الكود البريدي:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Zip/Postal Code:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.zipCode || customer?.postalCode || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>الرمز / الكود البريدي:</strong></td>
            </tr>
            {/* Phone Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Phone:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.phone || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>هاتف:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Phone:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.phone || customer?.mobileNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>هاتف:</strong></td>
            </tr>
            {/* VAT No Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>VAT Number:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{branch?.vatNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>رقم ضريبة القيمة المضافة:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>VAT Number:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>{customer?.vatNumber || customer?.vatNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>رقم ضريبة القيمة المضافة:</strong></td>
            </tr>
            {/* Other ID Row */}
            <tr>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Other Seller Id:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>CR# {branch?.crNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>معرف الآخر:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11 }}><strong>Other Buyer Id:</strong></td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, wordWrap: 'break-word', overflowWrap: 'break-word' }}>CR# {customer?.companyRegistrationNumber || customer?.companyRegistrationNo || '-'}</td>
              <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right' }}><strong>معرف الآخر:</strong></td>
            </tr>
          </tbody>
        </table>

        {/* Items table */}
        <table className="items-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>SR#</th>
              <th style={{ width: '36%' }}>ITEM & DESCRIPTION</th>
              <th style={{ width: '6%', textAlign: 'right' }}>QTY</th>
              <th style={{ width: '8%', textAlign: 'right' }}>RATE</th>
              <th style={{ width: '10%', textAlign: 'right' }}>ITEM AMT.</th>
              <th style={{ width: '6%', textAlign: 'right' }}>DISC (%)</th>
              <th style={{ width: '8%', textAlign: 'right' }}>DISC AMT.</th>
              <th style={{ width: '10%', textAlign: 'right' }}>TAXABLE AMOUNT.</th>
              <th style={{ width: '6%', textAlign: 'right' }}>VAT (%)</th>
              <th style={{ width: '6%', textAlign: 'right' }}>VAT AMT.</th>
              <th style={{ width: '10%', textAlign: 'right' }}>TOTAL {getBranchCurrency()}</th>
            </tr>
            <tr>
              <th style={{ width: '4%' }}>رقم</th>
              <th style={{ width: '36%' }}>البند و الوصف</th>
              <th style={{ width: '6%', textAlign: 'right' }}>الكمية</th>
              <th style={{ width: '8%', textAlign: 'right' }}>السعر</th>
              <th style={{ width: '10%', textAlign: 'right' }}>مبلغ البند</th>
              <th style={{ width: '6%', textAlign: 'right' }}>الخصم (%)</th>
              <th style={{ width: '8%', textAlign: 'right' }}>الخصم</th>
              <th style={{ width: '10%', textAlign: 'right' }}>المبلغ الخاضع للضريبة</th>
              <th style={{ width: '6%', textAlign: 'right' }}>ضريبة</th>
              <th style={{ width: '6%', textAlign: 'right' }}>قيمة الضريبة</th>
              <th style={{ width: '10%', textAlign: 'right' }}>إجمالي</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 16 }}>No line items available</td></tr>
            ) : (
              lines.map((line, idx) => (
                <tr key={line.lineId || idx}>
                  <td style={{ textAlign: 'center', fontWeight:700 }}>{idx+1}</td>
                  <td>{line.description || 'Item'}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.qty ?? 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.itemPrice)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.discountPercent)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.discountAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxableAmount)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxRate ?? line.taxPercent ?? 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatAmount(line.taxAmount)}</td>
                  <td style={{ textAlign: 'right', fontWeight:700 }}>{formatAmount(line.lineAmount)}</td>
                </tr>
              ))
            )}

            {/* totals row */}
            <tr className="items-total-row">
              <td colSpan={4} style={{ textAlign: 'right' }}>TOTAL</td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.subTotal)}</td>
              <td colSpan={2}></td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.taxableAmount)}</td>
              <td></td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.vatAmount)}</td>
              <td style={{ textAlign: 'right' }}>{formatAmount(totals.total)}</td>
            </tr>
          </tbody>
        </table>

        {/* Notes / Bank details / Summary */}
        <div style={{ display:'grid', gridTemplateColumns: '1fr 340px', gap:6, marginTop:0 }} className="print:gap-2 print:mt-0">
          <div>
            <div style={{ fontWeight:700, marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1"><strong>ملاحظات - Notes</strong></div>
            <div style={{ fontSize:9 }} className="print:text-xs">{creditNote.note || '—'}</div>

            <div style={{ marginTop:4 }} className="print:mt-2">
              <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs"><strong>Amount In Words:</strong></div>
              <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs">{amountWords || `Zero ${getBranchCurrencyWords().major} Only`}</div>
              <div style={{ fontSize:8, color:'#555', marginTop:2 }} className="print:text-[10px] print:mt-1">(All Amount in {getBranchCurrency()})</div>
            </div>

            {branch?.bankDetailEn && (
              <div style={{ marginTop:4 }} className="print:mt-2">
                <div style={{ fontWeight:700, fontSize:9 }} className="print:text-xs"><strong>Bank Detail</strong></div>
                <div style={{ whiteSpace:'pre-line', fontSize:9 }} className="print:text-xs">{branch.bankDetailEn}</div>
              </div>
            )}
          </div>

          <div className="summary-box print:p-2" style={{ border:'1px solid #F5F5F5', padding:6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>المجموع الفرعي - Sub Total</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.subTotal)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>Discount - خصم</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.totalDiscount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>المبلغ الخاضع للضريبة - Taxable Amount</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.taxableAmount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>ضريبة القيمة المضافة - VAT Amount</div>
              <div style={{ fontWeight:700 }}>{formatAmount(totals.vatAmount)}</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, fontSize:9 }} className="print:text-xs print:mb-1">
              <div style={{ fontWeight:700 }}>ضمان حسن التنفيذ - Retention</div>
              <div style={{ fontWeight:700 }}>0%</div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', borderTop:'2px solid #F5F5F5', paddingTop:4, marginTop:4, fontSize:10, fontWeight:800 }} className="print:text-xs print:pt-2 print:mt-2">
              <div>إجمالي المبلغ المستحق - Total Amount Due</div>
              <div>{formatAmount(totals.total)}</div>
            </div>
          </div>
        </div>
        </div>

        <div className="invoice-footer">This credit note was generated electronically and does not require any signature. | تم إنشاء هذا الإشعار إلكترونيًا ولا يحتاج إلى توقيع.</div>

      </div>
    </div>
  )
}
